import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import PageBreadcrumb from "@/components/PageBreadcrumb";

import Footer from "@/components/Footer";
import CallToAction from "@/components/CallToAction";
import Section from "@/components/Section";
import ServiceSection, { ServiceSectionData } from "@/components/ServiceSection";
import ServicesSideNav from "@/components/ServicesSideNav";
import { Lightbulb, Rocket, Compass, LucideIcon } from "lucide-react";

const serviceMeta: Array<{ id: string; index: number; icon: LucideIcon; deliverablesKey: "deliverablesHeading" | "questionsHeading" }> = [
  { id: "thought-leadership", index: 1, icon: Lightbulb, deliverablesKey: "deliverablesHeading" },
  { id: "transformation-enablement", index: 2, icon: Rocket, deliverablesKey: "deliverablesHeading" },
  { id: "executive-advisory", index: 3, icon: Compass, deliverablesKey: "questionsHeading" },
];

const Services = () => {
  const { t } = useTranslation();

  const services: ServiceSectionData[] = serviceMeta.map((m) => {
    const pricing = t(`servicesPage.items.${m.id}.pricing`, { defaultValue: "" });
    const packagesRaw = t(`servicesPage.items.${m.id}.packages`, {
      returnObjects: true,
      defaultValue: [],
    }) as ServiceSectionData["packages"];
    return {
      id: m.id,
      index: m.index,
      icon: m.icon,
      eyebrowLabel: t(`servicesPage.items.${m.id}.eyebrowLabel`),
      title: t(`servicesPage.items.${m.id}.title`),
      audience: t(`servicesPage.items.${m.id}.audience`),
      intro: t(`servicesPage.items.${m.id}.intro`),
      serviceDescription: t(`servicesPage.items.${m.id}.serviceDescription`),
      explorationPoints: t(`servicesPage.items.${m.id}.explorationPoints`, {
        returnObjects: true,
      }) as string[],
      deliverablesHeading: t(`servicesPage.ui.${m.deliverablesKey}`),
      process: t(`servicesPage.items.${m.id}.process`),
      result: t(`servicesPage.items.${m.id}.result`),
      pricing: pricing || undefined,
      packages: Array.isArray(packagesRaw) && packagesRaw.length > 0 ? packagesRaw : undefined,
    };
  });

  const navItems = services.map((s) => ({
    id: s.id,
    label: s.title,
    icon: s.icon,
  }));

  const serviceJsonLd = services.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.title,
    description: s.serviceDescription,
    provider: {
      "@type": "Organization",
      name: "AI-labbet",
      url: "https://ailabbet.com/",
    },
    areaServed: "SE",
    url: `https://ailabbet.com/services#${s.id}`,
  }));

  return (
    <>
      <SeoHead
        title={{
          en: "Our Business Areas | AI-labbet",
          sv: "Våra affärsområden | AI-labbet",
        }}
        description={{
          en: "AI-labbet's three complementary business areas: Thought leadership, Transformation & enablement, and Executive advisory.",
          sv: "AI-labbets tre kompletterande affärsområden: Thought leadership, Transformation & enablement och Executive advisory.",
        }}
        path="/services"
        jsonLd={serviceJsonLd}
      />

      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <PageBreadcrumb currentPage="services" />

        <main className="flex-1">
          <Section
            id="services-top"
            title={t("servicesPage.title")}
            titleAs="h1"
            description={t("servicesPage.heroLead")}
            containerSize="wide"
            grid
            className="overflow-visible"
          >
            <div className="lg:grid lg:grid-cols-[18rem_1fr] lg:gap-16 lg:items-start">
              <ServicesSideNav items={navItems} variant="sticky" />

              <div>
                <ServicesSideNav items={navItems} variant="inline" />
                {services.map((service) => (
                  <ServiceSection key={service.id} service={service} />
                ))}
              </div>
            </div>
          </Section>

          <CallToAction />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Services;
